/* eslint-disable react/no-array-index-key */
import React from 'react';
import { getFullDateInCustomTimezone } from 'utils/dateManagement';
import { getTranslatedData } from 'locales';
import { Loader } from 'components/indicators/loader';
import { getDeliveryTimeText } from '../helpers';

export const ProcessingOrdersList = ({
  currentTab,
  orders,
  onEditClicked,
  onProgressStatusChange,
  isLoadingProgress,
  companySettings,
}) => {
  const hasNextButton = (order) => {
    if (!order.progress_status || order.progress_status === 'New' || order.progress_status === 'In_Progress' || order.progress_status === 'Ready') {
      return true;
    }

    return false;
  };

  const renderCompleteText = (order) => {
    if (!order.progress_status || order.progress_status === 'New') {
      return 'Start';
    }

    if (order.progress_status === 'In_Progress') {
      if (companySettings.skip_ready) {
        return 'Complete';
      }

      return 'Finish';
    }

    if (order.progress_status === 'Ready') {
      return 'Complete';
    }

    return '';
  };

  const getStatusBarColor = (order) => {
    if (currentTab === 'new' || currentTab === 'in_progress') {
      if (order.prepare_status === 'late') {
        return 'bg-red-500';
      }

      if (order.prepare_status === 'future') {
        return 'bg-indigo-500';
      }

      if (order.prepare_status === 'now') {
        return 'bg-green-500';
      }
    }

    return null;
  };

  const isOtherStatusInTab = (order) => {
    if (currentTab === 'new' && (order.progress_status === 'In_Progress' || order.progress_status === 'Delivering')) {
      return true;
    }

    return false;
  };

  const renderOtherTabStatus = (order) => {
    let text;

    if (order.progress_status === 'In_Progress') {
      text = 'In Progress';
    }

    if (order.progress_status === 'Delivering') {
      text = 'Delivering';
    }

    if (text) {
      return <span className="text-sm text-gray-900 font-medium">{text}</span>;
    }

    return null;
  };

  const onProgressStatusChangeWrapper = (order) => {
    if (!order.progress_status || order.progress_status === 'New') {
      return onProgressStatusChange(order, 'In_Progress');
    }

    if (order.progress_status === 'In_Progress') {
      if (companySettings.skip_ready) {
        return onProgressStatusChange(order, 'Completed');
      }

      return onProgressStatusChange(order, 'Ready');
    }

    if (order.progress_status === 'Ready') {
      return onProgressStatusChange(order, 'Completed');
    }

    return '';
  };

  const renderLocationText = (order) => {
    if (order.is_delivery) {
      return 'Delivery';
    }

    if (order.is_takeout) {
      return 'Takeaway';
    }

    return `Table ${order.table?.number}`;
  };

  const renderTime = (order) => {
    return (
      <div>
        <div className="text-xs text-gray-600"><span className="">Placed</span>: {getFullDateInCustomTimezone(order.date_utc, order.timezone)}</div>
        <div className="text-sm text-gray-900"><span className="font-semibold">Due</span>: {order.delivery_date ? getDeliveryTimeText({ order, timezone: order.timezone, isPrepare: true }) : 'Now'}</div>
      </div>
    );
  };

  const renderOrders = () => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3 md:gap-y-6">
        {orders.map((order) => {
          const kitchenNote = order && order.payments && order.payments.length > 0 ? order.payments[0].kitchen_note : null;

          const isNextButtonLoading = isLoadingProgress[order._id];

          const statusBarColor = getStatusBarColor(order);

          const isOtherStatusOrder = isOtherStatusInTab(order);

          const providerOrderNumber = order.syncedOrder && order.syncedOrder.rkeeper_order_number;

          return (
            <div key={order._id} className="w-full">
              <div className="py-3 w-full bg-white border-gray-300 rounded-lg shadow inline-block overflow-hidden">
                {!!statusBarColor && <div className={`w-full h-2 ${statusBarColor} -mt-3 mb-3`} />}

                <div className="px-3">
                  <div className="flex flex-row justify-between">
                    <div className="mr-2">
                      {renderTime(order)}
                    </div>
                    <div className="text-base text-gray-900 font-medium">
                      #{order.number}{providerOrderNumber ? <span className="text-xs text-gray-400"> (#{providerOrderNumber})</span> : ''}
                    </div>
                  </div>

                  <div className="mt-3">
                    {!!order.syncedOrder && !!order.syncedOrder.products && order.syncedOrder.products.length > 0 &&
                  order.syncedOrder.products.map((x, i) => {
                    let variationsToShow = x.variations;

                    if (!variationsToShow && x.variations_json) {
                      variationsToShow = JSON.parse(x.variations_json);
                    }

                    return (
                      <div key={`${i}`} className="mt-2">
                        <div className="text-base text-gray-900">{x.quantity} x {getTranslatedData(x.product_name)}</div>
                        {!!variationsToShow && variationsToShow.length > 0 && variationsToShow.map((variation) => {
                          return (
                            <div key={variation.id} className="ml-2">
                              {!!variation.items && variation.items.length > 0 && variation.items.map((item) => {
                                return (
                                  <div key={item.id} className="text-gray-600 text-sm mt-[2px]">
                                    - {getTranslatedData(item.name)}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  </div>

                  <div className="border-b border-gray-100 mt-3" />

                  <div className="mt-3 text-sm text-gray-700">
                    {!!kitchenNote && (
                    <div className="mb-1">
                      <span className="font-semibold">
                        Note:{' '}
                      </span>
                      {kitchenNote}
                    </div>
                    )}

                    <div className="mb-1">
                      <span className="font-semibold">
                        Type:{' '}
                      </span>
                      {renderLocationText(order)}
                    </div>
                    {order.is_delivery && (
                    <div className="mb-1">
                      <span className="font-semibold">
                        Deliver On:{' '}
                      </span>
                      {order.delivery_date ? getDeliveryTimeText({ order, timezone: order.timezone, isPrepare: false }) : 'As soon as possible'}
                    </div>
                    )}
                    {order.is_delivery && (
                    <div className="mb-1">
                      <span className="font-semibold">
                        Client Name:{' '}
                      </span>
                      {order.delivery_name || '-'}
                    </div>
                    )}
                    {!!order.room_number && (
                    <div className="mb-1">
                      <span className="font-semibold">
                        Room Number:{' '}
                      </span>
                      {order.room_number || '-'}
                    </div>
                    )}
                  </div>

                  <div className="flex flex-row justify-between mt-5">
                    <button
                      type="button"
                      onClick={() => onEditClicked(order)}
                      className="bg-white py-1 px-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 inline-flex justify-center
                    items-center hover:bg-gray-50 disabled:opacity-50"
                      disabled={isNextButtonLoading}
                    >
                      Details
                    </button>
                    {hasNextButton(order) && (
                    <button
                      type="button"
                      onClick={() => onProgressStatusChangeWrapper(order)}
                      className="bg-indigo-600 border border-transparent rounded-md shadow-sm py-1 px-3 inline-flex justify-center items-center text-sm font-medium
                  text-white hover:bg-indigo-700 disabled:opacity-50"
                      disabled={isNextButtonLoading}
                    >
                      {isNextButtonLoading ? <Loader xxs /> : renderCompleteText(order)}
                    </button>
                    )}
                  </div>
                </div>

                {isOtherStatusOrder && <div className="bg-amber-50 border-t border-gray-200 w-full py-2 px-3 mt-3 -mb-3 text-center">{renderOtherTabStatus(order)}</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {renderOrders()}
    </div>
  );
};
